import { cn, cva, noddiColors, NoddiIcon, type VariantProps } from 'noddi-ui-common';
import { ButtonHTMLAttributes, PropsWithChildren } from 'react';

type NoddiSelectableBoxProps = {
  isSelected: boolean;
  setIsSelected: (isSelected: boolean) => void;
} & ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof selectableVariants>;

const selectableVariants = cva(
  cn(
    'flex w-full items-center justify-between rounded-2xl border-[1.5px] border-darkPurple30 text-start text-darkPurple transition-colors',
    'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-hidden',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50'
  ),
  {
    variants: {
      size: {
        medium: 'gap-3 px-[18px] py-4',
        small: 'gap-3 px-3 py-2'
      }
    },
    defaultVariants: {
      size: 'medium'
    }
  }
);

const NoddiSelectableBox = ({
  children,
  isSelected,
  setIsSelected,
  size,
  className,
  ...props
}: PropsWithChildren<NoddiSelectableBoxProps>) => (
  <button
    type='button'
    onClick={() => setIsSelected(!isSelected)}
    className={cn(
      selectableVariants({ size }),
      isSelected ? 'bg-purpleBg active:bg-[#F0EAF8]' : 'bg-white active:bg-darkPurpleGrey',
      className
    )}
    {...props}
  >
    <div className={cn('min-w-0 flex-1', isSelected ? 'font-semibold' : 'font-medium')}>{children}</div>
    <SelectableCheck isSelected={isSelected} size={size ?? 'medium'} />
  </button>
);

export { NoddiSelectableBox };

const SelectableCheck = ({ isSelected, size }: { isSelected: boolean; size: 'medium' | 'small' }) => (
  <span
    className={cn(
      'flex shrink-0 items-center justify-center rounded-full border-2 transition-colors',
      size === 'small' ? 'size-5' : 'size-6',
      isSelected ? 'border-darkPurple bg-darkPurple' : 'border-darkPurple40 bg-transparent'
    )}
  >
    <NoddiIcon
      name='Check'
      color={noddiColors.white}
      size={size === 'small' ? 12 : 14}
      className={cn(
        'transition-all duration-150 [transition-timing-function:cubic-bezier(0.2,0.7,0.3,1)]',
        isSelected ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
      )}
    />
  </span>
);
